import $ from "jquery";
import {Component, h} from "preact";
import {Loading} from "./Loading";
import {HorizontalNavigation} from "./Navigation/HorizontalNavigation";
import {Step} from "./Step";
import {OptionsStepContent} from "./Step/OptionsStepContent";
import {RuntimeStepContent} from "./Step/RuntimeStepContent";
import {SuccessStepContent} from "./Step/SuccessStepContent";
import {UserDataFormStepContent} from "./Step/UserDataFormStepContent";
import {OverviewStepContent} from "./Step/OverviewStepContent";
import {debounce} from "mojave/timing";
import {formatTechnicalDate, isNextMonth} from "../lib/date";


/**
 * @typedef {{
 *      url: string,
 * }} Membership.Props
 *
 * @typedef {{
 *      step: number,
 *      selectedRuntime: ?number,
 *      selectedOptions: Object<number, Object<number, boolean>>,
 *      formData: Membership.FormData,
 *      resultStoring: boolean,
 *      isFinished: boolean,
 *      finalization: {
 *          isLoading: boolean,
 *          result: ?{status: string},
 *      },
 *      horizontalNavigationActive: boolean,
 * }} Membership.State
 *
 * @typedef {{
 *     status: string,
 *     error: ?string,
 *     data: ?Membership.Contract,
 *     privacyPageUrl: ?string,
 * }} Membership.APIResponse
 *
 * @typedef {{
 *     id: number,
 *     length: number,
 *     title: string,
 *     recurringFee: number,
 *     recurringDescription: (null|string),
 *     entryFee: number,
 *     entryDescription: (null|string),
 * }} Membership.Runtime
 *
 * @typedef {{
 *     id: number,
 *     headline: string,
 *     required: boolean,
 *     selectionMethod: number,
 *     options: Membership.Option[],
 * }} Membership.OptionsCategory
 *
 * @typedef {{
 *     id: number,
 *     name: string,
 *     description: ?string,
 *     price: ?{
 *         amount: number,
 *         interval: number,
 *     },
 * }} Membership.Option
 *
 * @typedef {{
 *      contractStart: string,
 *      salutation: string,
 *      title: ?string,
 *      lastName: string,
 *      firstName: string,
 *      birthday: string,
 *      street: string,
 *      areaCode: string,
 *      city: string,
 *      phone: ?string,
 *      mobile: string,
 *      email: string,
 *      iban: string,
 *      bic: string,
 *      accountHolder: string,
 *      presenteeActive: string,
 *      presenteeName: string,
 *      presenteeBirthday: string,
 *      presenteeEmail: string,
 *      presenteeStreet: string,
 *      presenteeAreaCode: string,
 *      presenteeCity: string,
 * }} Membership.FormData
 *
 *
 * @typedef {{
 *      runtimes: Membership.Runtime[],
 *      optionsCategories: Membership.OptionsCategory[],
 *      firstDay: string|null,
 *      client: {
 *          name: string,
 *          personalization: {
 *              configuratorRegards: ?string,
 *              websiteCss: ?string,
 *              websiteNavigationHorizontal: boolean,
 *          }
 *      },
 * }} Membership.Contract
 */
export class Membership extends Component
{
	/**
	 * @private
	 * @type {?Membership.APIResponse}
	 */
	data = null;


	/**
	 * @private
	 * @constant
	 * @type {number}
	 */
	HORIZONTAL_NAVIGATION_MIN_WIDTH = 820;


	/**
	 * @inheritDoc
	 */
	constructor (props)
	{
		super(props);
		this.state = {
			step: 1,
			selectedRuntime: null,
			selectedOptions: {},
			formData: {},
			isFinished: false,
			horizontalNavigationActive: false,
			finalization: {
				isLoading: false,
				result: null,
			},
		};
	}


	/**
	 * @inheritDoc
	 */
	componentDidMount ()
	{
		this.fetchData();
	}


	/**
	 * Fetches the data of the configurator
	 *
	 * @private
	 */
	fetchData ()
	{
		/** @type {Membership.Props} props */
		const props = this.props;

		$.ajax(props.url, {
			method: "get",
			dataType: "json",
		})
			.done(
				/** @param {Membership.APIResponse} response */
				response =>
				{
					// set response + reset selection
					this.data = response;
					/** @type {Membership.State} newState */
					const newState = {};

					if (response.data != null)
					{
						const contract = response.data;

						if (contract.runtimes.length === 0)
						{
							this.data.status = "error";
							this.forceUpdate();
							return;
						}

						// calculate whether horizontal navigation is active
						newState.horizontalNavigationActive = this.isHorizontalNavigationActive();

						if (newState.horizontalNavigationActive)
						{
							$(window).on("resize", debounce(() => this.onResize(), 250));
						}

						// automatically select the first runtime
						newState.selectedRuntime = contract.runtimes[0].id;

						// append custom CSS
						if (null !== contract.client.personalization.websiteCss)
						{
							const style = document.createElement("style");
							style.type = "text/css";
							style.media = "screen";
							style.textContent = contract.client.personalization.websiteCss;
							document.head.appendChild(style);
						}
					}

					this.setState(newState);
				}
			)
			.fail(
				() =>
				{
					this.data = {
						status: "error",
					};
					this.forceUpdate();
				}
			);
	}


	/**
	 * @param {Membership.Props} props
	 * @param {Membership.State} state
	 * @returns {JSX.Element}
	 */
	render (props, state)
	{
		if (null === this.data)
		{
			return (
				<div class="_membership">
					<Loading/>
				</div>
			);
		}

		// the request failed due to any reason - just remove the widget
		if (this.data.status === "error")
		{
			return null;
		}

		const steps = this.getAvailableSteps();

		return (
			<div class="_membership">
				{state.horizontalNavigationActive && (
					<HorizontalNavigation
						steps={steps.map(step => step.headline)}
						activeStep={state.step}
						isDeactivated={state.isFinished}
						onDirectSelect={step => this.goToStep(step)}
					/>
				)}
				{this.renderSteps(steps)}
			</div>
		);
	}


	/**
	 * Returns a list of all available states
	 *
	 * @private
	 * @returns {Array<{headline : string, content : JSX.Element}>}
	 */
	getAvailableSteps ()
	{
		/** @type {Membership.State} state */
		const state = this.state;
		/** @type {Membership.Contract} contract */
		const contract = this.data.data;
		/** @type {Array<{headline: string, content: JSX.Element}>} */
		const steps = [];

		steps.push({
			headline: "Auswahl",
			content: (
				<RuntimeStepContent
					runtimes={contract.runtimes}
					selected={state.selectedRuntime}
					onSelect={runtime => this.setState({selectedRuntime: runtime.id})}
					onProceed={() => this.proceed()}
				/>
			),
		});

		if (contract.optionsCategories.length > 0)
		{
			steps.push({
				headline: "Optionen",
				content: (
					<OptionsStepContent
						optionsCategories={contract.optionsCategories}
						selected={state.selectedOptions}
						selectedRuntime={this.getSelectedRuntime()}
						onProceed={() => this.proceed()}
						onSelectOption={(categoryId, selection) => this.updateOptionsSelection(categoryId, selection)}
					/>
				),
			});
		}

		steps.push({
			headline: "Angaben",
			content: (
				<UserDataFormStepContent
					onProceed={formData => this.proceed({formData})}
					firstDay={contract.firstDay}
				/>
			),
		});

		steps.push({
			headline: "Bestätigung",
			content: (
				<OverviewStepContent
					contract={this.data.data}
					selectedRuntime={state.selectedRuntime}
					selectedOptions={state.selectedOptions}
					formData={state.formData}
					onProceed={(monthlyFee) => this.onRegistrationComplete(monthlyFee)}
					privacyPageUrl={this.data.privacyPageUrl}
					firstDay={this.data.data.firstDay}
				/>
			),
		});

		steps.push({
			headline: "Fertig",
			content: (
				<SuccessStepContent
					contract={this.data.data}
					finalization={state.finalization}
				/>
			),
		});

		return steps;
	}


	/**
	 * Renders the step containers
	 *
	 * @private
	 * @param {Array<{headline: string, content: JSX.Element}>} steps
	 * @returns {JSX.Element[]}
	 */
	renderSteps (steps)
	{
		/** @type {Membership.State} state */
		const state = this.state;

		return steps.map((step, index) =>
		{
			const stepIndex = index + 1;

			const onDirectSelect = (index >= steps.length - 1)
				? null
				: () => this.goToStep(stepIndex);

			return (
				<Step
					headline={step.headline}
					step={stepIndex}
					activeStep={state.step}
					isHeadlineVisible={!state.horizontalNavigationActive}
					onDirectSelect={onDirectSelect}
				>
					{step.content}
				</Step>
			);
		});
	}


	/**
	 * Updates the option selection
	 *
	 * @private
	 * @param {number} categoryId
	 * @param {Object<number, boolean>} selection
	 */
	updateOptionsSelection (categoryId, selection)
	{
		this.setState(
			/**
			 * @param {Membership.State} state
			 * @returns {Membership.State}
			 */
			state =>
			{
				state.selectedOptions[categoryId] = selection;

				return {
					selectedOptions: state.selectedOptions,
				};
			}
		);
	}


	/**
	 * Jumps to the given step
	 *
	 * @private
	 * @param {number} step
	 * @param {Object} additionalData
	 */
	goToStep (step, additionalData = {})
	{
		additionalData.step = step;
		this.setState(additionalData);
	}


	/**
	 * Proceeds to the next step
	 *
	 * @private
	 * @param {Object} additionalData
	 */
	proceed (additionalData = {})
	{
		/** @type {Membership.State} state */
		const state = this.state;
		this.goToStep(state.step + 1, additionalData);
	}


	/**
	 * Callback after the registration was completed by the user
	 *
	 * @private
	 * @param {number} monthlyFee
	 */
	onRegistrationComplete (monthlyFee)
	{
		this.setState(
			{
				isFinished: true,
				finalization: {
					isLoading: true,
					result: null,
				},
				// the steps are 1-indexed
				step: this.getAvailableSteps().length,
			},
			() =>
			{
				/** @type {Membership.State} state */
				const state = this.state;

				$.ajax(this.props.url, {
					method: "post",
					dataType: "json",
					data: {
						// @deprecated
						startDate: state.formData.contractStart,
						form: state.formData,
						options: state.selectedOptions,
						runtime: state.selectedRuntime,
						monthlyFee: monthlyFee,
					},
				})
					.done(data => this.onDataStorageResponse(data ? data.status : "error"))
					.fail(() => this.onDataStorageResponse("error"))
				;
			}
		);
	}


	/**
	 * Callback on when the data storage response arrived
	 *
	 * @param {string} status
	 */
	onDataStorageResponse (status)
	{
		this.setState({
			finalization: {
				isLoading: false,
				result: "ok" === status ? "ok" : "error",
			},
		});
	}


	/**
	 * Decides, whether the horizontal navigation is active
	 *
	 * @private
	 * @returns {boolean}
	 */
	isHorizontalNavigationActive ()
	{
		// if the feature is deactivated, just return false
		if (!this.data.data.client.personalization.websiteNavigationHorizontal)
		{
			return false;
		}

		// only activate it, if the min width is set
		return $(this.base).width() > this.HORIZONTAL_NAVIGATION_MIN_WIDTH;
	}


	/**
	 * Callback on when the page resizes
	 *
	 * @private
	 */
	onResize ()
	{
		/** @type {Membership.State} state */
		const state = this.state;

		const isVisible = this.isHorizontalNavigationActive();

		if (state.horizontalNavigationActive !== isVisible)
		{
			this.setState({horizontalNavigationActive: isVisible});
		}
	}


	/**
	 * @private
	 * @returns {?Membership.Runtime}
	 */
	getSelectedRuntime ()
	{
		const runtimes = this.data.data.runtimes;
		/** @type {Membership.State} state */
		const state = this.state;

		for (let i = 0; i < runtimes.length; i++)
		{
			if (runtimes[i].id === state.selectedRuntime)
			{
				return runtimes[i];
			}
		}

		return null;
	}
}
